import { t } from 'i18next';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import '../../../styles/cards/mcpStatusCard.scss';
import { linkSVG, settingSVG } from '../../../lib/allCustomSVGs';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useState,
} from 'react';
import { swapToBundledMcpLogo } from '../../../lib/helper-functions/mcpLogoFallback';

interface Props {
  info: string;
  active?: string;
  color?: string;
  icon?: any;
  iconTitle?: string;
  image?: any;
  leftIcon?: any;
  disabled?: boolean;
  // Disables ONLY the on/off toggle, leaving the Configure/Connect (reconnect)
  // path fully usable. Set when the credential is dead (reconnect_needed): the
  // user must be able to reconnect but must not be able to switch a broken
  // credential on or off.
  toggleDisabled?: boolean;
  hideConfigure?: boolean;
  onClick?: () => void;
  onCardClick?: () => void;
  onStatusToggle?: (newStatus: boolean) => void;
  hasOAuth?: boolean;
  isToggleHidden?: boolean;
  customPillLabel?: string;
  customPillColor?: string;
  // Optional footer rendered inside the card — used to embed the Meta
  // System-User connect CTA directly on the Facebook Ads card.
  extraContent?: ReactNode;
}

const MCPStatusCard = ({
  info,
  active,
  color,
  icon,
  image,
  leftIcon,
  disabled,
  toggleDisabled,
  hideConfigure,
  onClick,
  onCardClick,
  onStatusToggle,
  hasOAuth = false,
  isToggleHidden = false,
  iconTitle = '',
  customPillLabel = '',
  customPillColor = '',
  extraContent,
}: Props) => {
  const [isToggling, setIsToggling] = useState(false);

  // Configure / Connect are divs, so they need the same keyboard contract the
  // card body already implements: Enter and Space activate, and a disabled
  // affordance is neither focusable nor activatable.
  const handleAffordanceKeyDown = (e: ReactKeyboardEvent) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const affordanceStyle = {
    cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
  };

  const handleStatusToggle = async () => {
    if (!onStatusToggle || isToggling || disabled || toggleDisabled) return;

    try {
      setIsToggling(true);
      await onStatusToggle(active !== 'true');
    } catch (error) {
      console.error('Error toggling MCP status:', error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className='mcpStatusCard'>
      {icon && (
        <div title={iconTitle} className='mcpStatusCard__icon'>
          <CustomSVGs svg={icon} />
        </div>
      )}
      <div className='mcpStatusCard__content-holder'>
        {(leftIcon || image) && (
          <div
            className={`mcpStatusCard__image${
              leftIcon ? ' mcpStatusCard__image--leftIcon' : ''
            }`}
          >
            {leftIcon ? (
              <CustomSVGs svg={leftIcon} />
            ) : (
              <img src={image} alt='status' onError={swapToBundledMcpLogo} />
            )}
          </div>
        )}
        <div
          className='mcpStatusCard__content'
          onClick={onCardClick}
          role={onCardClick ? 'button' : undefined}
          tabIndex={onCardClick ? 0 : undefined}
          onKeyDown={
            onCardClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick();
                  }
                }
              : undefined
          }
          style={
            onCardClick ? { cursor: 'pointer', outline: 'none' } : undefined
          }
        >
          <h2
            style={{
              color:
                color === 'green'
                  ? '#28a745'
                  : color === 'red'
                  ? '#FF4433'
                  : color === 'yellow'
                  ? '#f79640'
                  : 'var(--main-text-color)',
            }}
          >
            {info}
          </h2>

          <div className='mcpStatusCard__inner-content-holder'>
            <div className='mcpStatusCard__inner-content-holder-left'>
              {active !== undefined && (
                <>
                  <div
                    data-testid='mcp-status-pill'
                    data-mcp-status={
                      customPillColor
                        ? customPillColor
                        : active === 'true'
                        ? 'green'
                        : active === 'false'
                        ? 'yellow'
                        : 'red'
                    }
                    className='mcpStatusCard__inner-content-holder-left-tag-status'
                  >
                    <span>
                      {customPillLabel
                        ? customPillLabel
                        : active === 'true'
                        ? t('Connected')
                        : active === 'false'
                        ? t('Deactivated')
                        : t('NotConnected')}
                    </span>
                  </div>
                  {!hideConfigure && (
                    <>
                      {active === 'true' || active === 'false' ? (
                        <div
                          className='mcpStatusCard__inner-content-holder-left-tag-config'
                          data-has-icon={icon ? 'true' : 'false'}
                          onClick={disabled ? undefined : onClick}
                          role='button'
                          aria-label={t('MCP-ConfigureAria', {
                            mcpName: info,
                          })}
                          aria-disabled={disabled ? 'true' : undefined}
                          tabIndex={disabled ? undefined : 0}
                          onKeyDown={
                            disabled ? undefined : handleAffordanceKeyDown
                          }
                          style={affordanceStyle}
                        >
                          {icon ? (
                            <CustomSVGs svg={icon} />
                          ) : (
                            <CustomSVGs svg={settingSVG} />
                          )}
                          <span>{t('Configure')}</span>
                        </div>
                      ) : (
                        <div
                          className='mcpStatusCard__inner-content-holder-left-tag-connect'
                          onClick={disabled ? undefined : onClick}
                          role='button'
                          aria-label={t('MCP-ConnectAria', { mcpName: info })}
                          aria-disabled={disabled ? 'true' : undefined}
                          tabIndex={disabled ? undefined : 0}
                          onKeyDown={
                            disabled ? undefined : handleAffordanceKeyDown
                          }
                          style={affordanceStyle}
                        >
                          {hasOAuth && <CustomSVGs svg={linkSVG} />}
                          <span>{t('Connect')}</span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div
              className='mcpStatusCard__inner-content-holder-right'
              onClick={(e) => e.stopPropagation()}
            >
              {(active === 'true' || active === 'false') && (
                <div className='mcpStatusCard__inner-content-holder-right-toggle'>
                  {!isToggleHidden && (
                    <ToggleSwitch
                      isOn={active === 'true'}
                      onClick={handleStatusToggle}
                      disabled={isToggling || disabled || toggleDisabled}
                      message={
                        toggleDisabled ? t('MCP-ReconnectNeeded') : undefined
                      }
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {extraContent && (
        <div
          className='mcpStatusCard__extra'
          onClick={(e) => e.stopPropagation()}
        >
          {extraContent}
        </div>
      )}
    </div>
  );
};

export default MCPStatusCard;
